import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Col, Row } from "react-flexbox-grid";

import theme from "../../theme";
import { customMq } from "../../utils/mediaQuery";
import Button from "../Button";
import Heading from "../Heading";
import Icon from "../Icon";
import Link from "../Link";
import { h4 } from "../Typography";
import PrinciplesItem, { principlesItem } from "./PrinciplesItem";

const Wrapper = styled.div`
  position: relative;
`;

const StyledRow = styled(Row)`
  flex-direction: column-reverse;
  ${customMq(768)} {
    flex-direction: row;
  }
`;

const HeadingWrapper = styled.div`
  margin-bottom: ${theme.spacing("m")};
`;

const Sidebox = styled.div`
  margin-right: ${theme.space("m")};
`;

const StyledLink = styled(Link)`
  color: black;
`;

const SideLink = styled.span`
  ${h4};
  display: inline-block;

  padding-bottom: ${theme.space("xxs")};
  margin-top: ${theme.space("l")};

  ${customMq(768)} {
    margin-top: 0;
  }
`;

const Item = styled.div`
  position: relative;
  cursor: pointer;
  &:before,
  &:after {
    content: "";
    z-index: 5;
    display: block;
    position: absolute;
    width: 100%;
    height: 1px;
    background-color: ${theme.color("dark.lighter")};
  }
`;

const ItemHeading = styled.div`
  display: flex;

  padding: 2rem 0;
`;

const StyledHeading = styled(Heading)`
  z-index: 10;
  margin-right: auto;
`;

const HeadingCount = styled.span`
  margin-right: ${theme.spacing("xs")};
  color: ${theme.color("primary")};
`;

function Principles({ title, cta, items, ...props }) {
  const [current, setCurrent] = useState(false);

  return (
    <Wrapper {...props}>
      <StyledRow bottom="md">
        <Col mdOffset={0} md={3}>
          {cta && (
            <Sidebox>
              <SideLink>{cta.body}</SideLink>
              <StyledLink to={cta.path}>
                <Button styling="link" color="secondary" as="span">
                  <span>{cta.label}</span>
                  <Icon type="arrow" />
                </Button>
              </StyledLink>
            </Sidebox>
          )}
        </Col>
        <Col mdOffset={0} md={9}>
          <HeadingWrapper>
            <Heading size={2}>{title}</Heading>
          </HeadingWrapper>
          <div>
            {items.map(({ title }, index) => (
              <Item
                key={`principleItem-${index}`}
                onMouseEnter={() => setCurrent(index)}
                onMouseLeave={() => setCurrent(false)}
              >
                <ItemHeading>
                  <StyledHeading size={3}>
                    <HeadingCount>
                      {(index + 1).toLocaleString(undefined, {
                        minimumIntegerDigits: 2,
                      })}
                    </HeadingCount>
                    {title}
                  </StyledHeading>
                </ItemHeading>
              </Item>
            ))}
            {current !== false && <PrinciplesItem {...items[current]} />}
          </div>
        </Col>
      </StyledRow>
    </Wrapper>
  );
}

Principles.propTypes = {
  title: PropTypes.string,
  cta: PropTypes.shape({
    label: PropTypes.string,
    path: PropTypes.string,
    body: PropTypes.string,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      ...principlesItem,
    })
  ),
};

Principles.defaultProps = {};

export default Principles;
